import type { Editor } from '@tiptap/core'
import { useCallback, useEffect, useRef, useState } from 'react'
import styled from 'styled-components'

import Icon from '../../general/Icon'
import Modal from '../../molecules/Modal'

type Props = {
  editor: Editor
}

type SpeechRecognitionLike = {
  continuous: boolean
  interimResults: boolean
  lang: string
  start: () => void
  stop: () => void
  abort: () => void
  onresult: ((ev: SpeechRecognitionResultEvent) => void) | null
  onerror: ((ev: Event) => void) | null
  onend: (() => void) | null
}

/** Minimal shape for `SpeechRecognition` result events (DOM lib may omit in some builds). */
type SpeechRecognitionResultEvent = Event & {
  resultIndex: number
  results: Array<{ isFinal: boolean; 0: { transcript: string } }>
}

function getSpeechRecognitionCtor(): (new () => SpeechRecognitionLike) | null {
  if (typeof window === 'undefined') return null
  if (!window.isSecureContext) return null
  const w = window as unknown as {
    SpeechRecognition?: new () => SpeechRecognitionLike
    webkitSpeechRecognition?: new () => SpeechRecognitionLike
  }
  return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null
}

/**
 * Bottom-right actions matching the legacy rich editor: dictation (Web Speech API)
 * and clear document. TipTap has no built-in speech — this uses the browser API.
 */
export function RichEditorFooterActions({ editor }: Props) {
  const [listening, setListening] = useState(false)
  const recognitionRef = useRef<SpeechRecognitionLike | null>(null)

  const stopRecognition = useCallback(() => {
    recognitionRef.current?.stop()
    recognitionRef.current = null
    setListening(false)
  }, [])

  useEffect(() => {
    return () => stopRecognition()
  }, [stopRecognition])

  const toggleSpeech = useCallback(() => {
    const Ctor = getSpeechRecognitionCtor()
    if (!Ctor) return

    if (listening) {
      stopRecognition()
      return
    }

    const recognition = new Ctor()
    recognition.continuous = true
    recognition.interimResults = false
    recognition.lang =
      typeof navigator !== 'undefined' && navigator.language
        ? navigator.language
        : 'en-GB'

    recognition.onresult = (event: SpeechRecognitionResultEvent) => {
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const result = event.results[i]
        if (result.isFinal) {
          const text = result[0]?.transcript ?? ''
          if (text.trim()) {
            editor.chain().focus().insertContent(`${text.trim()} `).run()
          }
        }
      }
    }

    recognition.onerror = () => {
      stopRecognition()
    }

    recognition.onend = () => {
      recognitionRef.current = null
      setListening(false)
    }

    recognitionRef.current = recognition
    recognition.start()
    setListening(true)
  }, [editor, listening, stopRecognition])

  const clearAll = useCallback(() => {
    editor.chain().focus().clearContent().run()
  }, [editor])

  const [clearModalOpen, setClearModalOpen] = useState(false)

  const speechSupported = getSpeechRecognitionCtor() !== null

  return (
    <Footer role="group" aria-label="Editor tools">
      {speechSupported ? (
        <SpeechRoundButton
          type="button"
          title={listening ? 'Stop dictation' : 'Speech to text'}
          aria-pressed={listening}
          $recording={listening}
          onClick={() => toggleSpeech()}
        >
          {listening ? (
            <StopSquare aria-hidden />
          ) : (
            <Icon name="AudioOutlined" />
          )}
        </SpeechRoundButton>
      ) : null}
      <RoundIconButton
        type="button"
        title="Clear all"
        onClick={() => setClearModalOpen(true)}
      >
        <Icon name="DeleteOutlined" />
      </RoundIconButton>

      <Modal
        classicPadding
        closeButton
        isModalVisible={clearModalOpen}
        setIsModalVisible={setClearModalOpen}
        localisation={{
          title: <ClearModalTitle>Clear editor</ClearModalTitle>,
          confirmText: 'Clear',
          cancelText: 'Cancel'
        }}
        onConfirm={clearAll}
        onCancel={() => setClearModalOpen(false)}
        confirmButtonProps={{ variant: 'danger' }}
        testId="rich-editor-clear"
      >
        <ClearModalBody>
          Are you sure you want to clear the editor?
        </ClearModalBody>
      </Modal>
    </Footer>
  )
}

/** Overrides default Modal title (pink / uppercase) for a neutral confirm dialog. */
const ClearModalTitle = styled.span`
  display: block;
  font-size: 18px;
  font-weight: 600;
  line-height: 1.3;
  text-transform: none !important;
  color: ${({ theme }) => theme.color.neutral.color2};
`

const ClearModalBody = styled.p`
  margin: 0;
  font-size: 15px;
  line-height: 1.5;
  color: ${({ theme }) => theme.color.neutral.color3};
`

/** Solid square — classic media “stop” affordance (not an outlined glyph). */
const StopSquare = styled.span`
  display: block;
  width: 12px;
  height: 12px;
  border-radius: 1px;
  flex-shrink: 0;
  background-color: ${({ theme }) => theme.color.neutral.color11};
`

const Footer = styled.div`
  position: absolute;
  right: 10px;
  bottom: 10px;
  display: flex;
  gap: 8px;
  z-index: 0;
  pointer-events: auto;
`

const RoundIconButton = styled.button<{ $active?: boolean }>`
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  padding: 0;
  border-radius: 50%;
  border: 1px solid ${({ theme }) => theme.color.neutral.color7};
  background: ${({ $active, theme }) =>
    $active ? theme.color.neutral.color8 : theme.color.neutral.color10};
  color: ${({ theme }) => theme.color.neutral.color2};
  cursor: pointer;
  display: flex;

  &:hover {
    background: ${({ theme }) => theme.color.neutral.color8};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.color.status.primary};
    outline-offset: 1px;
  }
`

/** Red “recording” state with solid stop square; idle state matches neutral round buttons. */
const SpeechRoundButton = styled(RoundIconButton)<{ $recording: boolean }>`
  border-color: ${({ $recording, theme }) =>
    $recording ? theme.color.status.danger : theme.color.neutral.color7};
  background: ${({ $recording, theme }) =>
    $recording ? theme.color.status.danger : theme.color.neutral.color10};
  color: ${({ $recording, theme }) =>
    $recording ? theme.color.neutral.color11 : theme.color.neutral.color2};

  &:hover {
    background: ${({ $recording, theme }) =>
      $recording ? theme.color.press.danger : theme.color.neutral.color8};
    border-color: ${({ $recording, theme }) =>
      $recording ? theme.color.press.danger : theme.color.neutral.color7};
  }

  &:focus-visible {
    outline-color: ${({ $recording, theme }) =>
      $recording ? theme.color.status.danger : theme.color.status.primary};
  }
`
